import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { format, isValid, parseISO } from 'date-fns'
import { EmtTextField, EmtTextFieldProps } from 'emt-components-v2'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { Calendar } from './Calendar'

type DatePickerFieldProps = Omit<EmtTextFieldProps, 'type' | 'value' | 'onChange' | 'nativePicker'> & {
  /** Selected day, as yyyy-MM-dd ('' when empty) */
  value?: string
  onChange: (value: string) => void
  /** Earliest selectable day, as yyyy-MM-dd */
  min?: string
  /** Latest selectable day, as yyyy-MM-dd */
  max?: string
}

const toDate = (value?: string): Date | null => {
  if (!value) return null
  const date = parseISO(value)
  return isValid(date) ? date : null
}

const monthOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)

const isTouchDevice = () => !window.matchMedia('(hover: hover) and (pointer: fine)').matches

/** Nearest ancestor scrolling vertically (e.g. a full screen modal while the page is locked), else the page */
const getScrollParent = (element: HTMLElement): HTMLElement => {
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement
}

/** Scrolls so the opened calendar is fully visible, without pushing its field out of view */
const scrollCalendarIntoView = (calendar: HTMLElement, field: HTMLElement) => {
  const MARGIN = 16
  const container = getScrollParent(field)
  const isPage = container === document.scrollingElement || container === document.documentElement
  const bounds = isPage
    ? { top: 0, bottom: window.visualViewport?.height ?? window.innerHeight }
    : container.getBoundingClientRect()
  const calendarRect = calendar.getBoundingClientRect()
  const fieldRect = field.getBoundingClientRect()

  let delta = 0
  if (calendarRect.bottom + MARGIN > bounds.bottom) {
    // Calendar under the field, cut at the bottom: scroll down, at most until the field reaches the top
    delta = Math.max(0, Math.min(calendarRect.bottom + MARGIN - bounds.bottom, fieldRect.top - bounds.top - MARGIN))
  } else if (calendarRect.top - MARGIN < bounds.top) {
    // Calendar flipped above the field, cut at the top: scroll up
    delta = calendarRect.top - MARGIN - bounds.top
  }
  if (delta) container.scrollBy({ top: delta, behavior: 'smooth' })
}

/**
 * Date field opening a calendar under it wherever it is pressed. Unlike the
 * browser's picker, the calendar doesn't take the focus, so the date can still
 * be typed while it is open (except on touch devices, where there is no typing).
 */
export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value = '',
  onChange,
  min,
  max,
  fullWidth = true,
  sx,
  inputProps,
  onFocus,
  onKeyDown,
  ...rest
}) => {
  const { isPetsittingMode } = usePetsittingMode()
  const anchorRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  // Read after mount, to render the same as the server first
  const [isTouch, setIsTouch] = useState(false)
  useEffect(() => setIsTouch(isTouchDevice()), [])
  const selectedDate = toDate(value)
  const minDate = toDate(min)
  const [currentMonth, setCurrentMonth] = useState(() => monthOf(selectedDate ?? minDate ?? new Date()))

  // Show the month of the typed date
  useEffect(() => {
    if (selectedDate) setCurrentMonth(monthOf(selectedDate))
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const openCalendar = () => {
    if (open || rest.disabled) return
    setCurrentMonth(monthOf(selectedDate ?? minDate ?? new Date()))
    setOpen(true)
  }

  const handleDateClick = (day: number, monthOffset = 0) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, day)
    onChange(format(date, 'yyyy-MM-dd'))
    setOpen(false)
  }

  return (
    // Closes on click/tap outside: blur isn't reliable on mobile, where tapping the calendar can blur the input
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      {/* `cursor: pointer` makes iOS send the tap to the box, as the field ignores it there */}
      <Box ref={anchorRef} onMouseDown={openCalendar} sx={{ cursor: 'pointer' }}>
        <EmtTextField
          {...rest}
          type="date"
          nativePicker={false}
          fullWidth={fullWidth}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => {
            openCalendar()
            onFocus?.(e)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter' || e.key === 'Tab') setOpen(false)
            onKeyDown?.(e)
          }}
          sx={[
            { '& input': { height: '20px', WebkitAppearance: 'none', margin: '8px 0' } },
            // On touch devices the input must never get the focus, or the phone's own
            // date wheel opens over the calendar (iOS ignores `readOnly` for that):
            // taps go through the field to the box, which opens the calendar
            isTouch && { pointerEvents: 'none' },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          inputProps={{
            ...inputProps,
            min,
            max,
            onClick: (e: React.MouseEvent<HTMLInputElement>) => {
              // Stops the browser's picker, opened by a click on its icon (or anywhere on Firefox)
              e.preventDefault()
              inputProps?.onClick?.(e)
            },
            readOnly: isTouch || inputProps?.readOnly,
            style: { lineHeight: 'normal', ...inputProps?.style },
          }}
        />
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
          // Once positioned, scroll so the whole calendar is visible (on mobile it can open half off screen)
          popperOptions={{
            onFirstUpdate: () => {
              if (calendarRef.current && anchorRef.current) {
                scrollCalendarIntoView(calendarRef.current, anchorRef.current)
              }
            },
          }}
        >
          {/* Prevents the input from losing the focus, so typing keeps working */}
          <Box
            ref={calendarRef}
            onMouseDown={(e) => e.preventDefault()}
            sx={{
              '--calendar-accent': (theme: any) => theme.palette[isPetsittingMode ? 'purple' : 'red'].main,
              width: '320px',
              maxWidth: 'calc(100vw - 32px)',
              marginTop: '4px',
              padding: '6px 16px 16px',
              backgroundColor: '#fff',
              border: '1px solid #E8E8EA',
              borderRadius: '18px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Calendar
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              selectedRange={{ start: selectedDate, end: selectedDate }}
              hoveredDate={null}
              minDate={minDate ?? undefined}
              maxDate={toDate(max) ?? undefined}
              // Only `min` limits past days, like on the input itself
              disablePast={false}
              monthYearPicker
              handleDateClick={handleDateClick}
            />
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
